import React, { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import * as api from '../../api';
import { isNativeApp, requestGoogleLogin } from '../../utils/nativeApp';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function ManageLogin({ matchId, match, onAuth }) {
  const { t } = useTranslation();
  const { signIn, signUp } = useAuth();

  const [mode, setMode] = useState('code'); // 'code' | 'email' | 'signup'
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isDemo = match?.alias?.startsWith('demo-');

  // Handle native auth token from Google sign-in callback
  useEffect(() => {
    const nativeToken = sessionStorage.getItem('nativeAuthToken');
    if (!nativeToken) return;
    sessionStorage.removeItem('nativeAuthToken');
    sessionStorage.removeItem('nativeAuthName');
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Extract the Google ID token from com.google|<token>
        const idToken = nativeToken.startsWith('com.google|') ? nativeToken.slice('com.google|'.length) : nativeToken;
        const res = await api.staffGoogleAuth(matchId, idToken);
        sessionStorage.setItem('accessToken', res.data.accessToken);
        try {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          const me = { email: payload.email, name: payload.name || payload.email };
          sessionStorage.setItem('me', JSON.stringify(me));
        } catch { /* ignore */ }
        onAuth();
      } catch (err) {
        setError(err.response?.data?.detail || t('manage.googleLoginFailed', 'Google login failed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [matchId, onAuth, t]);

  const handleCodeLogin = async (e) => {
    e.preventDefault();
    const pwd = code || (isDemo ? 'password' : '');
    if (!pwd) {
      setError(t('manage.codeRequired', 'Please enter a staff code'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await api.staffCodeAuth(matchId, pwd);
      sessionStorage.setItem('accessToken', res.data.accessToken);
      onAuth();
    } catch (err) {
      setError(err.response?.data?.detail || t('manage.invalidCode', 'Invalid staff code'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t('manage.emailPasswordRequired', 'Please enter email and password'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      await signIn({ email, password });
      onAuth();
    } catch (err) {
      setError(err.response?.data?.detail || t('manage.loginFailed', 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t('manage.emailPasswordRequired', 'Please enter email and password'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signUp({ email, password, name: email });
      onAuth();
    } catch (err) {
      setError(err.response?.data?.detail || t('manage.signUpFailed', 'Sign up failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCallback = useCallback(async (response) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.staffGoogleAuth(matchId, response.credential);
      sessionStorage.setItem('accessToken', res.data.accessToken);
      try {
        const payload = JSON.parse(atob(response.credential.split('.')[1]));
        const me = { email: payload.email, name: payload.name || payload.email };
        sessionStorage.setItem('me', JSON.stringify(me));
      } catch { /* ignore */ }
      onAuth();
    } catch (err) {
      setError(err.response?.data?.detail || t('manage.googleLoginFailed', 'Google login failed. Make sure your Google account is registered as staff.'));
    } finally {
      setLoading(false);
    }
  }, [matchId, onAuth, t]);

  const inNativeApp = isNativeApp();

  useEffect(() => {
    if (inNativeApp) return; // Skip GIS in native app WebView
    const init = () => {
      if (!window.google?.accounts?.id) return false;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
      });
      const el = document.getElementById('manage-google-btn');
      if (el) {
        window.google.accounts.id.renderButton(el, {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'signin_with',
        });
      }
      return true;
    };

    if (!init()) {
      const interval = setInterval(() => {
        if (init()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleGoogleCallback, inNativeApp]);

  const matchTitle = match?.title || matchId;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      }}
    >
      {/* Background image with dark overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url(https://endofscoring.s3.eu-central-1.amazonaws.com/login-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
          },
        }}
      />

      {/* Login card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'rgba(255,255,255,0.92)',
          borderRadius: '12px',
          boxShadow: '0px 0px 20px rgba(0,0,0,0.5)',
          p: 4,
          maxWidth: 400,
          width: '90vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, textAlign: 'center' }}>
          {matchTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {t('manage.staffLogin', 'Staff Login')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {mode === 'code' ? (
          <Box component="form" onSubmit={handleCodeLogin} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <TextField
              autoFocus
              fullWidth
              label={t('manage.staffCode', 'Staff Code')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            {isDemo && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {t('manage.demoHint', 'For demo matches, use "password"')}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ height: 48 }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? t('manage.signingIn', 'Signing In...') : t('manage.signIn', 'Sign In')}
            </Button>
            <Button
              variant="text"
              size="small"
              sx={{ mt: 1, textTransform: 'none' }}
              onClick={() => { setMode('email'); setError(''); }}
            >
              {t('manage.useEmailPassword', 'Sign in with email & password')}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={mode === 'signup' ? handleSignUp : handleEmailLogin} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <TextField
              autoFocus
              fullWidth
              label={t('auth.email', 'Email')}
              type="email"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('auth.password', 'Password')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ height: 48 }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading
                ? (mode === 'signup' ? t('manage.signingUp', 'Signing Up...') : t('manage.signingIn', 'Signing In...'))
                : (mode === 'signup' ? t('auth.signUp', 'Sign Up') : t('manage.signIn', 'Sign In'))}
            </Button>
            <Button
              variant="text"
              size="small"
              sx={{ mt: 1, textTransform: 'none' }}
              onClick={() => { setMode(mode === 'signup' ? 'email' : 'signup'); setError(''); }}
            >
              {mode === 'signup'
                ? t('manage.useSignIn', 'Already have an account? Sign in')
                : t('manage.useSignUp', 'Create a new account')}
            </Button>
            <Button
              variant="text"
              size="small"
              sx={{ mt: 0.5, textTransform: 'none' }}
              onClick={() => { setMode('code'); setError(''); }}
            >
              {t('manage.useStaffCode', 'Sign in with staff code')}
            </Button>
          </Box>
        )}

        <Divider sx={{ width: '100%', my: 2 }}>{t('manage.or', 'or')}</Divider>

        {inNativeApp ? (
          <Button
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={() => {
              sessionStorage.setItem('nativeAuthReturnTo', window.location.pathname);
              requestGoogleLogin();
            }}
            sx={{
              width: 280, height: 44, textTransform: 'none', fontWeight: 500,
              bgcolor: '#4285F4', '&:hover': { bgcolor: '#3367D6' },
            }}
          >
            {t('manage.signInWithGoogle', 'Sign in with Google')}
          </Button>
        ) : (
          <Box id="manage-google-btn" />
        )}
      </Box>
    </Box>
  );
}
